// Client-side helpers for the "guest audit" conversion path: a logged-out
// visitor pastes a URL, we run a real single-page desktop audit immediately,
// then gate the result behind login + claim.
//
// The per-job guest secret is the read + claim credential. We keep it in
// localStorage keyed by jobId so a page refresh (or returning from the login
// step) keeps access, and so the /try/[id] page and the claim call can both
// read it. It's never put in the URL (it would leak into history / referrers).

import { startGuestAudit } from "./api";
import { readClientId } from "./auth";

const KEY_PREFIX = "snap-site:guest-audit:";

export interface StoredGuestAudit {
  jobId: string;
  guestToken: string;
}

export function saveGuestAudit(jobId: string, guestToken: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY_PREFIX + jobId, guestToken);
  } catch {
    // Private mode / quota — non-fatal; the visitor just loses cross-refresh
    // access to this one preview.
  }
}

export function readGuestToken(jobId: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(KEY_PREFIX + jobId);
  } catch {
    return null;
  }
}

export function clearGuestAudit(jobId: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(KEY_PREFIX + jobId);
  } catch {
    /* non-fatal */
  }
}

// Accept bare domains ("stripe.com") as well as full URLs. Mirrors the input
// the hero form accepts.
export function normalizeUrl(raw: string): string {
  const t = raw.trim();
  if (!t) return "";
  return /^https?:\/\//i.test(t) ? t : `https://${t}`;
}

// Client mirror of backend/src/url-validation.ts (checkAuditUrl). Kept in
// lockstep BY HAND — the backend module imports node:dns/node:net so it can't
// be shared directly. Same design rules apply: STRUCTURAL checks only (the
// URL parser + a has-a-dot rule), fail toward allowing, never rewrite what
// the user typed. The server re-validates authoritatively pre-charge; this
// copy exists so a typo'd guest gets an instant inline answer with zero
// network round-trip ("easy mails.cc" → suggestion "easymails.cc").
export type AuditUrlValidation =
  | { ok: true; url: string; hostname: string }
  | { ok: false; message: string; suggestion?: string };

export function validateAuditUrl(raw: string): AuditUrlValidation {
  const t = raw.trim();
  if (!t) return { ok: false, message: "Enter a URL to audit." };

  if (/\s|%20/i.test(t)) {
    const candidate = t.replace(/%20/gi, "").replace(/\s+/g, "");
    const retry = candidate ? validateAuditUrl(candidate) : null;
    return {
      ok: false,
      message: "Web addresses can't contain spaces.",
      ...(retry?.ok
        ? { suggestion: candidate.replace(/^https?:\/\//i, "") }
        : {}),
    };
  }

  let u = parseUrl(t);
  if (u && !/^https?:$/.test(u.protocol)) {
    if (t.includes("://")) {
      return {
        ok: false,
        message:
          "Only http(s) websites can be audited — enter an address like yoursite.com.",
      };
    }
    // "localhost:3000"-style host:port parses as a scheme; retry as a host.
    u = parseUrl(`https://${t}`);
    if (!u) {
      return {
        ok: false,
        message:
          "Only http(s) websites can be audited — enter an address like yoursite.com.",
      };
    }
  }
  if (!u) {
    u = parseUrl(`https://${t}`);
    if (!u) {
      return {
        ok: false,
        message:
          "That doesn't look like a web address. Check it for typos or stray characters — e.g. yoursite.com.",
      };
    }
  }

  const hostname = u.hostname;
  const host = hostname.replace(/\.$/, "");
  if (host.length > 253 || host.split(".").some((l) => l.length > 63)) {
    return {
      ok: false,
      message:
        "That doesn't look like a web address. Check it for typos or stray characters — e.g. yoursite.com.",
    };
  }
  // Bare IPv4 targets are unusual but legitimate — let the run decide.
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) {
    return { ok: true, url: u.toString(), hostname };
  }
  if (!host.includes(".") || (host.split(".").pop() ?? "").length < 2) {
    return {
      ok: false,
      message: "Enter a full web address, like yoursite.com.",
    };
  }
  return { ok: true, url: u.toString(), hostname };
}

function parseUrl(s: string): URL | null {
  try {
    return new URL(s);
  } catch {
    return null;
  }
}

// Starts a guest audit and persists its token. Returns the new jobId; the
// caller navigates to /try/[jobId]. Throws ApiError on failure (empty URL,
// out-of-credits 402, network/5xx) so the form can surface a message.
export async function startGuestAuditFlow(rawUrl: string): Promise<string> {
  const url = normalizeUrl(rawUrl);
  if (!url) throw new Error("Enter a URL to audit.");
  const { jobId, guestToken } = await startGuestAudit({
    url,
    clientId: readClientId(),
  });
  if (guestToken) saveGuestAudit(jobId, guestToken);
  return jobId;
}
